import type {ExtensionAPI} from '@wix/document-manager-core'
import type {CompRef, IdMap, IdMapValue} from '@wix/document-services-types'
import {getNextIdForPrefix} from './aiExtensionContent'

import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {ViewerExtensionAPI} from '../viewer/viewerExtension'
import {fileInfoDomain, infoPath, generationTimeBudget, ERRORS} from './constants'
import type {StructureExtensionAPI} from '../structure'
import {predicateBuilderComp} from './aiBuilderUtils'
import {ReportableError} from '@wix/document-manager-utils'
import type {LoggerExtAPI} from '../logger'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import {DATA_TYPES} from '../../constants/constants'

interface MediaPropsInfo {
    idMapValue: IdMapValue
    data: any
    width: number
    height: number
}

type MediaMeasurements = Record<string, {width: number; height: number}>

interface MediaMap {
    idMap: IdMap
    originalData: Record<string, any>
    mediaMeasurements: MediaMeasurements
}

const getMediaPropsInfo = (
    extensionAPI: ExtensionAPI,
    componentPointer: CompRef,
    mediaType: string
): MediaPropsInfo[] => {
    const {manifest} = extensionAPI as ManifestExtensionAPI & InnerElementsExtensionAPI & ViewerExtensionAPI
    const mediaPropsInfo: MediaPropsInfo[] = []
    const mediaProps = manifest.getData(componentPointer, true, [{prop: mediaType}])
    mediaProps.forEach(context => {
        const {prop, path, value: data, nestedDataPath} = context
        const measurements = calculateImageMeasurements(extensionAPI, componentPointer, path)
        mediaPropsInfo.push({
            idMapValue: {pointer: componentPointer, prop, path, nestedDataPath} as IdMapValue,
            data,
            width: measurements.width,
            height: measurements.height
        })
    })
    return mediaPropsInfo
}

export const createMediaMap = (
    extensionAPI: ExtensionAPI,
    componentPointers: CompRef[],
    mediaType: string,
    caller: string,
    typeCounter: Record<string, number> = {}
): MediaMap => {
    const mediaMeasurements: MediaMeasurements = {}
    const idMap: IdMap = {}
    const originalData: Record<string, any> = {}
    for (const componentPointer of componentPointers) {
        const componentType = (extensionAPI as StructureExtensionAPI).components.getComponentType(componentPointer)
        if (!predicateBuilderComp(extensionAPI, componentType)) {
            ;(extensionAPI as LoggerExtAPI).logger.captureError(
                new ReportableError({
                    errorType: ERRORS.BUILDER.NOT_BUILDER_COMPONENT,
                    message: 'Not a builder comp'
                }),
                {tags: {caller}, extras: {componentPointer, componentType}}
            )
            continue
        }
        const mediaPropsInfo = getMediaPropsInfo(extensionAPI, componentPointer, mediaType)
        for (const mediaPropInfo of mediaPropsInfo) {
            const mappedId = getNextIdForPrefix(mediaType, typeCounter)
            mediaMeasurements[mappedId] = mediaPropInfo
            idMap[mappedId] = mediaPropInfo.idMapValue
            originalData[mappedId] = mediaPropInfo.data
        }
    }

    return {idMap, originalData, mediaMeasurements}
}

export const getMediaInfo = async (serverFacade: any, id: string): Promise<any> => {
    return await serverFacade.fetch(`${fileInfoDomain}/${id}/${infoPath}`, 'GET')
}

export const postForMedia = async <T>(
    serverFacade: any,
    endpoint: string,
    paramsMap: Record<string, any>
): Promise<T> => {
    const {message} = await serverFacade.post(endpoint, paramsMap, {
        'X-Time-Budget': generationTimeBudget
    })
    return JSON.parse(message)
}

const DEFAULT_MEASUREMENTS = {
    width: 1024,
    height: 768
}

export function calculateImageMeasurements(
    extensionAPI: ExtensionAPI,
    compRef: CompRef,
    innerElementPath?: string[]
): {width: number; height: number} {
    const {viewer, dataModel, manifest} = extensionAPI as ViewerExtensionAPI &
        DataModelExtensionAPI &
        ManifestExtensionAPI

    const viewerMeasurements =
        innerElementPath && innerElementPath.length > 0
            ? viewer.getInnerElementMeasurements(compRef, innerElementPath)
            : viewer.getComponentMeasurements(compRef)
    if (viewerMeasurements?.width && viewerMeasurements?.height) {
        return viewerMeasurements
    }

    const staleMeasurements = dataModel.components.getItem(compRef, DATA_TYPES.measurements)
    if (staleMeasurements?.width && staleMeasurements?.height) {
        return staleMeasurements
    }

    const manifestMeasurements = manifest.layout.getInitialSize(compRef)
    const width = manifestMeasurements?.width?.pixels
    const height = manifestMeasurements?.height?.pixels
    if (width && height) {
        return {width, height}
    }

    return DEFAULT_MEASUREMENTS
}
